import React from "react";
import { TextField, Grid, Checkbox, FormControlLabel, Button, Box } from "@mui/material";
import { Autocomplete } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import { DATES_VALUE } from "../../../../../../constants";
import { SelectByDateWithCustomPeriod } from "../../../../../../components/ui";
import { ICountriesToShowOption, ITypePurchase } from "../../utils";
import { IPlanOption } from "../../../../../../components/ui/DiscountsModal/PlansOptions";
import { SalesByPeriodPerCountry } from "../../../../../../api/billing/index.types";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface IProps {
  getReport: (startDate: string, endDate: string, isAllTime?: boolean) => Promise<void>;
  setIsLoading: (isLoading: boolean) => void;
  setData: (data: SalesByPeriodPerCountry[]) => void;
  setCountriesToShow: (c: ICountriesToShowOption[]) => void;
  setPlanToShow: (p: IPlanOption[]) => void;
  planToShow: IPlanOption[];
  plansOptions: IPlanOption[];
  countriesToShow: ICountriesToShowOption[];
  salesPerCountry: Record<string, number>;
  purchaseType: ITypePurchase;
  setPurchaseType: (p: ITypePurchase) => void;
  handleExportCsv: () => void;
}
const Controls: React.FC<IProps> = ({
  getReport,
  setIsLoading,
  setData,
  setCountriesToShow,
  setPlanToShow,
  planToShow,
  plansOptions,
  countriesToShow,
  salesPerCountry,
  purchaseType,
  setPurchaseType,
  handleExportCsv,
}) => {
  const handleChangeSelectedCountry = (
    _: React.ChangeEvent<unknown>,
    newValue: ICountriesToShowOption[]
  ) => setCountriesToShow(newValue);

  const handleChangePlan = (_: React.ChangeEvent<unknown>, newValue: IPlanOption[]) =>
    setPlanToShow(newValue);

  const handleChangePurchaseType = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPurchaseType({ ...purchaseType, [event.target.name]: event.target.checked });
  };

  const countriesOptions = Object.keys(salesPerCountry)
    .sort((a, b) => a.localeCompare(b))
    .map((c) => ({
      value: c,
      label: c,
    }));

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Box display="flex">
            <SelectByDateWithCustomPeriod
              getReport={getReport}
              setIsLoading={setIsLoading}
              setReport={setData}
              initialDateBy={DATES_VALUE.thisWeek}
            />

            <Button sx={{ ml: "10px" }} onClick={handleExportCsv}>
              Export CSV
            </Button>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ display: "grid", gridTemplateColumns: "2fr 2fr" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={purchaseType.isShowPurchase}
                  onChange={handleChangePurchaseType}
                  name="isShowPurchase"
                />
              }
              label="Show new purchases"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={purchaseType.isShowRebill}
                  onChange={handleChangePurchaseType}
                  name="isShowRebill"
                />
              }
              label="Show rebils"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={purchaseType.isShowResets}
                  onChange={handleChangePurchaseType}
                  name="isShowResets"
                />
              }
              label="Show resets"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={purchaseType.isShowFreeResets}
                  onChange={handleChangePurchaseType}
                  name="isShowFreeResets"
                />
              }
              label="Show free resets"
            />
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            onChange={handleChangePlan}
            fullWidth
            value={planToShow}
            options={plansOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => (
              <TextField {...params} variant="standard" label="Plans" placeholder="Plans" />
            )}
            renderOption={(props, option, state) => {
              return (
                <Box width="100%" component="li" {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={state.selected}
                  />
                  {option.label}
                </Box>
              );
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Autocomplete
            disableCloseOnSelect
            onChange={handleChangeSelectedCountry}
            fullWidth
            multiple
            value={countriesToShow}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={(option) => option.value}
            options={countriesOptions}
            renderInput={(params) => (
              <TextField {...params} variant="standard" label="Countries" placeholder="Countries" />
            )}
            renderOption={(props, option, state) => (
              <React.Fragment>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={state.selected}
                />
                {option.label}
              </React.Fragment>
            )}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default Controls;
